import React, { useState, useContext } from "react";
import "./home-style.css";
import LanguageContext from "../../context/LanguageContext";

import jsonDataEn from "./data/home-data-en.json";
import jsonDataFr from "./data/home-data-fr.json";

const Contact = ({ recipient }) => {
  const { language } = useContext(LanguageContext);
  const data = language === "en" ? jsonDataEn.data.contact : jsonDataFr.data.contact;

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    agree: false,
  });
  const [errors, setErrors] = useState({}); // {name, email, agree}
  const [status, setStatus] = useState({ type: "", msg: "" });
  const [loading, setLoading] = useState(false);

  // Required: name, email, agree (T&C). Message is optional.
  const validate = (vData) => {
    const e = {};
    if (!vData.name || !vData.name.trim()) e.name = data.nameValidation;
    if (!vData.email || !vData.email.trim()) e.email = data.emailValidation;
    if (!vData.agree) e.agree = data.tacValidation;
    return e;
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    const next = { ...form, [name]: type === "checkbox" ? checked : value };
    setForm(next);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });

    const eObj = validate(form);
    setErrors(eObj);
    if (Object.keys(eObj).length) {
      setStatus({ type: "error", msg: data.error });
      return;
    }

    setLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        message: (form.message || "").trim(), // message may be empty
        ...(recipient ? { recipient } : {}), // optional; backend can ignore
      };

      const res = await fetch(`${API_BASE}/api/contact/home`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // Be resilient to non-JSON HTML error responses
      const ct = res.headers.get("content-type") || "";
      const body = ct.includes("application/json") ? await res.json() : await res.text();

      if (!res.ok) {
        const msg = typeof body === "string" ? body : body?.error || "Failed to send.";
        throw new Error(msg);
      }

      setStatus({ type: "success", msg: data.success });
      setForm({ name: "", email: "", message: "", agree: false });
      setErrors({});
    } catch (err) {
      setStatus({ type: "error", msg: err.message || data.fail });
    } finally {
      setLoading(false);
    }
  };

  const disabled = loading; // only disable while sending

  return (
    <>
      <section className="contact" id="contact">
        <div className="contact__overlay" />
        <div className="contact__inner">
          {/* LEFT */}
          <div className="contact__left">
            <h2 className="contact__title">{data.title}</h2>
            <p className="contact__lead">{data.description}</p>

            <ul className="contact__links">
              <li>
                <svg className="contact__icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M3 5h18v14H3z" fill="none" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M3 6l9 7 9-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <a href="mailto:contact@flxdeck.com">contact@flxdeck.com</a>
              </li>
              <li>
                <svg className="contact__icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M6.6 2.5l2.2 3.9-1.9 1.9a15.3 15.3 0 007.8 7.8l1.9-1.9 3.9 2.2c.3.2.5.5.5.9v3.1c0 .6-.5 1.1-1.1 1.1A18.9 18.9 0 013 5.6c0-.6.5-1.1 1.1-1.1h3.1c.4 0 .7.2.9.5z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <a href="tel:+15143588519">(514) 358-8519</a>
              </li>
            </ul>
          </div>

          {/* RIGHT - FORM */}
          <form className="contact__form" onSubmit={onSubmit} noValidate>
            <label htmlFor="name">{data.name}</label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={onChange}
              required
              placeholder={data.namePlaceholder}
              aria-invalid={!!errors.name}
              className={errors.name ? "invalid" : ""}
            />
            {errors.name && <p className="field-error">{errors.name}</p>}

            <label htmlFor="email">{data.email}</label>
            <input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              required
              placeholder={data.emailPlaceholder}
              aria-invalid={!!errors.email}
              className={errors.email ? "invalid" : ""}
            />
            {errors.email && <p className="field-error">{errors.email}</p>}

            <label htmlFor="message">{data.message}</label>
            <textarea id="message" name="message" rows={6} value={form.message} onChange={onChange} placeholder={data.messgePlaceholder /* keep key as provided */} />
            {/* message is optional; no error message */}

            <label className="contact__check">
              <input type="checkbox" name="agree" checked={form.agree} onChange={onChange} aria-invalid={!!errors.agree} className={errors.agree ? "invalid" : ""} />
              <span>{data.tac}</span>
            </label>
            {errors.agree && <p className="field-error">{errors.agree}</p>}

            <button className="contact__btn" type="submit" disabled={disabled}>
              {loading ? data.loading : data.notLoading}
            </button>

            {status.msg && <p className={`contact__status ${status.type}`}>{status.msg}</p>}
          </form>
        </div>

        <div className="contact__brand">
          <img src="assets/flxdeck/flxdeck-logo.png" alt="FlxDeck" />
        </div>

        <hr className="contact__divider" />

        <footer className="contact__footer">
          <span>{data.rights}</span>
          <nav>
            <a href={data.privacyLink}>{data.privacy}</a>
            <a href={data.txLink} className="sep">
              {data.tc}
            </a>
          </nav>
        </footer>
      </section>
    </>
  );
};

export default Contact;
